import {useSpaceAvailability} from '../../hooks/useWebSocket';
import { useEffect, useRef, useState } from 'react';
import {
  fetchOcupacionMensual,
  OcupacionMensualDia,
} from '../../services/espacioServiciosService.ts';

export interface MonthlySpaceOccupancyState {
  occupiedDates: Set<string>;
  daysByDate: Record<string, OcupacionMensualDia>;
  loading: boolean;
  error: string | null;
  contextKey: string | null;
}

const emptyState = (): MonthlySpaceOccupancyState => ({
  occupiedDates: new Set<string>(),
  daysByDate: {},
  loading: false,
  error: null,
  contextKey: null,
});

export function useMonthlySpaceOccupancy(
  spaceId: number | null | undefined,
  month: string,
  refreshKey: unknown = 0,
): MonthlySpaceOccupancyState {
  const [liveRevision,setLiveRevision] = useState(0);
  useSpaceAvailability(() => { if (spaceId) setLiveRevision(value => value + 1); });
  const generation = useRef(0);
  const [state, setState] = useState<MonthlySpaceOccupancyState>(emptyState);

  useEffect(() => {
    const requestGeneration = ++generation.current;
    if (!spaceId || !/^\d{4}-\d{2}$/.test(month)) {
      setState(emptyState());
      return;
    }

    const contextKey = `${spaceId}|${month}`;
    setState(previous => previous.contextKey === contextKey
      ? { ...previous, loading: true, error: null }
      : { ...emptyState(), contextKey, loading: true });
    fetchOcupacionMensual(spaceId, month)
      .then(response => {
        if (generation.current !== requestGeneration) return;
        const daysByDate = Object.fromEntries(
          response.dias_ocupados.map(day => [day.fecha, day]),
        );
        setState({
          occupiedDates: new Set(response.dias_ocupados.map(day => day.fecha)),
          daysByDate,
          loading: false,
          error: null,
          contextKey,
        });
      })
      .catch(() => {
        if (generation.current !== requestGeneration) return;
        setState(previous => previous.contextKey === contextKey
          ? {
              ...previous,
              loading: false,
              error: 'No se pudo cargar la ocupación. Intenta nuevamente.',
            }
          : {
              ...emptyState(),
              contextKey,
              error: 'No se pudo cargar la ocupación. Intenta nuevamente.',
            });
      });

    return () => {
      if (generation.current === requestGeneration) generation.current += 1;
    };
  }, [spaceId, month, refreshKey, liveRevision]);

  return state;
}

